import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { MapPin, Mail, ArrowLeft, Briefcase } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { PageHeader, StatTile, Avatar, MatchScore } from '@/lib/atsUi'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataList } from '@/components/ui/data-list'
import { Tabs } from '@/components/ui/tabs'
import { EmptyState } from '@/components/states'
import {
  clientById,
  jobs,
  candidates,
  currentTenant,
  currentUser,
  STAGES,
  type JobStatus,
  type Stage,
} from '@/lib/mocks'
import { atsNav } from '@/lib/atsNav'

export const Route = createFileRoute('/clients/$clientId')({
  component: ClientDetailPage,
})

const STATUS_BADGE: Record<JobStatus, { label: string; variant: 'success' | 'warning' | 'info' | 'error' }> = {
  open: { label: 'Open', variant: 'success' },
  on_hold: { label: 'On hold', variant: 'warning' },
  filled: { label: 'Filled', variant: 'info' },
  draft: { label: 'Draft', variant: 'error' },
}

const STAGE_LABEL: Record<Stage, string> = Object.fromEntries(
  STAGES.map((s) => [s.key, s.label]),
) as Record<Stage, string>

const STAGE_VARIANT: Record<Stage, 'success' | 'info' | 'warning'> = {
  sourced: 'info',
  screened: 'info',
  l1: 'warning',
  l2: 'warning',
  offer: 'success',
  joined: 'success',
}

function ClientDetailPage() {
  const { clientId } = Route.useParams()
  const client = clientById(clientId)
  const [tab, setTab] = useState<'roles' | 'candidates'>('roles')

  if (!client) {
    return (
      <DashboardLayout title="Client" navSections={atsNav} tenant={currentTenant} user={currentUser}>
        <EmptyState title="Client not found" />
      </DashboardLayout>
    )
  }

  const clientJobs = jobs.filter((j) => j.client === client.name)
  const clientJobIds = new Set(clientJobs.map((j) => j.id))
  const clientCandidates = candidates.filter((c) => clientJobIds.has(c.jobId))

  return (
    <DashboardLayout title="Client" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="mb-4">
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Clients
        </Link>
      </div>

      <PageHeader
        title={client.name}
        subtitle="Roles, candidates, and terms for this client."
        action={
          <Button size="sm" asChild><Link to="/jobs/new">New job</Link></Button>
        }
      />

      {/* Header card */}
      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{client.name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span>{client.industry}</span>
            <span aria-hidden>·</span>
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {client.location}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="font-medium text-foreground">{client.contactName}</span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <Mail className="h-3.5 w-3.5" />
            {client.contactEmail}
          </span>
        </div>
        <div className="flex flex-wrap gap-4 text-sm pt-1 border-t border-border">
          <div>
            <span className="text-muted-foreground">Fee: </span>
            <span className="font-medium text-foreground">{client.feePct}%</span>
          </div>
          <div>
            <span className="text-muted-foreground">Guarantee: </span>
            <span className="font-medium text-foreground">{client.guaranteeDays} days</span>
          </div>
          <div>
            <span className="text-muted-foreground">Client since: </span>
            <span className="font-medium text-foreground">{client.since}</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-4 mt-6">
        <StatTile label="Open roles" value={client.openRoles} />
        <StatTile label="Active candidates" value={client.activeCandidates} />
      </div>

      {/* Roles + candidates — tabbed (one table at a time) */}
      <div className="mt-8">
        <Tabs
          items={[
            { value: 'roles', label: 'Roles', count: clientJobs.length },
            { value: 'candidates', label: 'Candidates', count: clientCandidates.length },
          ]}
          value={tab}
          onChange={(v) => setTab(v as 'roles' | 'candidates')}
        />

        {tab === 'roles' && (
          <DataList
            items={clientJobs}
            getKey={(job) => job.id}
            pageSize={5}
            empty="No roles for this client."
            renderRow={(job) => {
              const s = STATUS_BADGE[job.status]
              return (
                <Link
                  to="/jobs/$jobId"
                  params={{ jobId: job.id }}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground truncate">{job.title}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {job.location}
                    </span>
                    <span>{job.ctcRange}</span>
                    <Badge variant={s.variant}>{s.label}</Badge>
                  </div>
                </Link>
              )
            }}
          />
        )}

        {tab === 'candidates' && (
          <DataList
            items={clientCandidates}
            getKey={(c) => c.id}
            pageSize={5}
            empty="No candidates in pipeline yet."
            renderRow={(c) => (
              <Link
                to="/candidates/$candidateId"
                params={{ candidateId: c.id }}
                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={c.name} className="h-8 w-8 text-[11px]" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.title}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={STAGE_VARIANT[c.stage]}>{STAGE_LABEL[c.stage]}</Badge>
                  <MatchScore score={c.matchScore} />
                </div>
              </Link>
            )}
          />
        )}
      </div>
    </DashboardLayout>
  )
}
